import { useEffect, useRef, useState } from 'react';

import { activePin } from './pinnedAdvance';
import { type DraftEntry, rowIdOf } from './state/loggerDraftTypes';

// #308: which row the athlete pinned. Owned above the Modal beside the cursor
// (LG-13), as the rest hand-over reads it. One pin per session; a pin that
// stops holding is dropped. #318: a new live session starts on its first
// exercise pinned, decided once per session.
export type PinnedExercise = {
  pinnedId: string | null;
  setPinnedId: (id: string | null) => void;
};

export type PinSeed = {
  /** Live logging (`mode === 'log'`): the only mode with a rest to come back from. */
  live: boolean;
  /** The session key of the last draft restored from storage. */
  restoredKeyRef: { current: string | null };
};

export function usePinnedExercise(
  sessionKey: string,
  entries: readonly DraftEntry[],
  { live, restoredKeyRef }: PinSeed,
): PinnedExercise {
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const seededKey = useRef<string | null>(null);
  useEffect(() => {
    if (seededKey.current === sessionKey) return;
    const first = entries[0];
    const logged = entries.some((e) => e.sets.some((s) => s.completedAt !== null));
    const skip = !live || restoredKeyRef.current === sessionKey || logged;
    // An empty live session has nothing to decide yet: wait for its first row.
    if (!skip && first === undefined) {
      setPinnedId(null);
      return;
    }
    seededKey.current = sessionKey;
    setPinnedId(skip || first === undefined ? null : rowIdOf(first));
  }, [sessionKey, entries, live, restoredKeyRef]);
  const holds = activePin(pinnedId, entries) !== null;
  useEffect(() => {
    // Functional, so a stale pin cannot clear the seed queued above.
    if (pinnedId !== null && !holds) {
      setPinnedId((cur) => (cur === pinnedId ? null : cur));
    }
  }, [pinnedId, holds]);
  return { pinnedId, setPinnedId };
}
